// Import necessary Angular core modules and components
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
} from '@angular/core'
import { CommonModule } from '@angular/common'

import { IconValuesType } from '../icon/icon.component.types'
import { Color, IconComponent } from '../icon/icon.component'
import {
  TypographyComponent,
  TypographySize,
} from '../typography/typography.component'

import {
  ButtonSizeType,
  ButtonColorType,
  ButtonVariantType,
  ButtonIconPositionType,
} from './button.component.types'

@Component({
  selector: 'hyperkit-button', // The selector used to identify this component in templates
  standalone: true, // Indicates that this is a standalone component
  imports: [CommonModule, TypographyComponent, IconComponent], // Required imports for this component
  templateUrl: './button.component.html', // The HTML template file for this component
  styleUrls: ['./button.component.scss'], // The SCSS styles file for this component
  changeDetection: ChangeDetectionStrategy.OnPush, // Uses OnPush change detection strategy for better performance
})
export class ButtonComponent {
  // ========================
  // Input Properties
  // ========================

  /**
   * A custom image to display inside the button.
   * Can be a string (image URL) or a TemplateRef for advanced use.
   */
  @Input() customImage?: string

  /**
   * The visual style of the button.
   * Options: 'standard', 'ghost', 'icon', 'link', 'collapsable'.
   * @default 'standard'
   */
  @Input() variant: ButtonVariantType = 'standard'

  /**
   * The size of the button.
   * Options: 'small', 'standard'.
   * @default 'standard'
   */
  @Input() size: ButtonSizeType = 'medium'

  /**
   * The color theme of the button.
   * Options: 'primary', 'secondary', 'success', 'info', 'warning', 'destructive', 'inherit'.
   * @default 'inherit'
   */
  @Input() color: ButtonColorType = 'tertiary'

  @Input() iconColor?: Color

  /**
   * The text to display inside the button.
   * Optional for some button variants like 'icon'.
   */
  @Input() text?: string

  /**
   * The name of the icon to display in the button.
   * Optional unless the button variant requires it (e.g., 'icon').
   */
  @Input() icon?: IconValuesType

  /**
   * The position of the icon relative to the button text.
   * Options: 'left', 'right'.
   * @default 'left'
   */
  @Input() iconPosition: ButtonIconPositionType = 'left'

  /**
   * Whether the button is disabled and non-interactive.
   * @default false
   */
  @Input() disabled = false

  /**
   * 	If true, the button will take up the full width of its container.
   * @default false
   */
  @Input() fullWidth = false

  /**
   * Whether the button is in an expanded state (for collapsable buttons).
   * @default false
   */
  @Input() isExpanded = false

  /**
   * If set, the button renders as an `<a>` element with this URL. Same styling
   * applies. Preserves link semantics (right/middle/cmd-click, screen readers).
   */
  @Input() href?: string

  /**
   * Anchor target. Only applies when `href` is set.
   * @default '_self'
   */
  @Input() target: '_self' | '_blank' | '_parent' | '_top' = '_self'

  /**
   * Anchor rel. Only applies when `href` is set; only emitted when target is `_blank`.
   * @default 'noopener'
   */
  @Input() rel: 'noopener' | 'noreferrer' | 'none' = 'noopener'

  /**
   * Accessibility label. Applied to either the button or anchor element.
   */
  @Input() ariaLabel = ''

  /**
   * Computed rel attribute — only emit rel for external (_blank) navigation.
   */
  get hostRel(): string | null {
    if (!this.href || this.target !== '_blank' || this.rel === 'none') {
      return null
    }
    return this.rel
  }

  // ========================
  // Output Properties
  // ========================

  /**
   * Event emitted when the button is clicked.
   * Emits a MouseEvent.
   */
  @Output() buttonClick = new EventEmitter<MouseEvent>()

  // ========================
  // Dynamic Class Generation
  // ========================

  /**
   * Dynamically generates CSS classes for the button based on its properties.
   * Combines the color, size, and variant into specific class names that can
   * be styled in the component's SCSS file.
   */
  get classes(): string[] {
    const classes = ['btn']

    // Add size class FIRST to ensure it applies correctly
    if (this.size) {
      classes.unshift(`btn-${this.size}`) // Higher priority
    }

    // Add fullWidth class
    if (this.fullWidth) {
      classes.push(`btn-full-width`)
    }

    // Add variant class
    if (this.variant) {
      classes.push(`btn-${this.variant}`)
    }

    // Add color class
    if (this.color) {
      classes.push(`btn-${this.color.toLowerCase()}`)
    }

    // Add icon presence class LAST to prevent override issues
    if (this.icon) {
      classes.push('btn-with-icon')
      classes.push(
        this.iconPosition === 'right' ? 'btn-icon-right' : 'btn-icon-left',
      )
    } else {
      classes.push('btn-no-icon')
    }

    return classes
  }

  get resolvedIconColor(): Color {
    if (this.iconColor) {
      return this.iconColor
    }

    // Default behavior: inherit button text color
    return 'inherit'
  }

  get textSize(): TypographySize {
    return this.size === 'x-small' ? 'sm' : 'md'
  }

  // ========================
  // Event Handlers
  // ========================

  /**
   * Handles the click event on the button.
   * Prevents event emission if the button is disabled.
   *
   * @param event - The MouseEvent triggered by the click.
   */
  handleClick(event: MouseEvent): void {
    if (!this.disabled) {
      this.buttonClick.emit(event) // Emit the click event if the button is enabled
    }
  }
}
